import React, { useCallback, useMemo, useState } from "react";
import { queryCache } from "react-query";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import { ActionBar } from "../../../../components/ActionBar/ActionBar";
import { QUERY_KEYS } from "../../../../constants";
import { useWizard } from "../../../wizard/context";
import { ICourseListItemBackEndModel, CourseExpandedState } from "../../types";
import { Accordion, AccordionDetails, AccordionSummary } from "./Accordion";
import { parseCourseListItemToFrontEndModel } from "../../helpers/parseCourseListItemToFrontEndModel";
import theme from "../../../../themes";
import { CourseFinance } from "./CourseFinance";
import { useLanguageCoursesContext } from "../../pages/LanguageCoursesPage";

const useStyles = makeStyles(() => ({
  mainContainer: {
    border: "1px solid #e8eaf0",
    overflow: "hidden",
    "& .MuiPaper-elevation1": {
      padding: "unset",
    },
  },
  degreeName: {
    fontSize: 16,
    fontWeight: "bold",
    color: " #535358",
    paddingRight: 8,
  },
  degreeWord: {
    fontSize: 14,
    fontWeight: "bold",
    color: " #828190",
    paddingRight: 16,
  },
  majorsCount: {
    fontSize: 14,
    color: "#828190",
  },
  content: {
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      borderBottom: "1px solid #dfdcd6",
    },
  },
  labeledContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    "& > :first-child": {
      fontSize: 14,
      fontWeight: "bold",
      color: " #828190",
    },
    "& > :last-child": {
      fontSize: 14,
      color: " #535358",
    },
  },
  gridContainer: {
    maxWidth: 800,
  },
  link: {
    fontWeight: "bold",
    color: "#2f80ed",
    "&:hover": {
      color: "#1368d8",
      cursor: "pointer",
    },
  },
  disabled: {
    opacity: 0.3,
    background: "#f8f9fb",
    pointerEvents: "none",
  },
}));

export function Course({ course }: { course: ICourseListItemBackEndModel }) {
  const courseFront = useMemo(
    () => parseCourseListItemToFrontEndModel(course),
    [course],
  );
  const classes = useStyles();
  const { openWizard } = useWizard();
  const [courseExpandedState, setCourseExpandedState] =
    useState<CourseExpandedState>(CourseExpandedState.CLOSED);
  const {
    refetchLanguageCourses,
    languageCourseWizardConfigs: {
      getViewConfig,
      getActivateConfig,
      getDeactivateConfig,
      getEditConfig,
      getDeleteConfig,
    },
  } = useLanguageCoursesContext();

  const handlePriceClick = () => {
    if (courseExpandedState !== CourseExpandedState.PRICE) {
      setCourseExpandedState(CourseExpandedState.PRICE);
    } else {
      setCourseExpandedState(CourseExpandedState.CLOSED);
    }
  };

  const { schoolStudentApplications } = QUERY_KEYS;

  const changeCourseStatus = useCallback(async () => {
    if (courseFront.isActive) {
      (await openWizard(getDeactivateConfig(courseFront.id))) &&
        (await refetchLanguageCourses());
      await queryCache.invalidateQueries([schoolStudentApplications]);
    } else {
      (await openWizard(getActivateConfig(courseFront.id))) &&
        (await refetchLanguageCourses());
      await queryCache.invalidateQueries([schoolStudentApplications]);
    }
  }, [courseFront]);

  const deleteCourse = useCallback(async () => {
    (await openWizard(getDeleteConfig(courseFront.id))) &&
      (await refetchLanguageCourses());
  }, [courseFront]);

  const viewCourse = useCallback(
    () => openWizard(getViewConfig(courseFront.id)),
    [courseFront],
  );

  const editCourse = useCallback(async () => {
    (await openWizard(getEditConfig(courseFront.id))) &&
      (await refetchLanguageCourses());
  }, [courseFront]);

  const housingTypes = courseFront.housingTypes?.length
    ? courseFront.housingTypes?.join(", ")
    : "N/A";

  return (
    <div>
      <Accordion
        className={classes.mainContainer}
        expanded={!!courseExpandedState}
        TransitionProps={{
          timeout: 0,
        }}
      >
        <AccordionSummary>
          <Grid
            container
            spacing={2}
            alignItems={"center"}
            className={classes.gridContainer}
          >
            <Grid item xs={12} sm={6} md={3} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Category</div>
                <div>
                  <OverflowTooltip text={courseFront.category} />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Course Type</div>
                <div>{courseFront.courseType}</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Age Range</div>
                <div>{courseFront.ageRange}</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Housing Type</div>
                <div>
                  <OverflowTooltip text={housingTypes} />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Price</div>
                <div>
                  <span className={classes.link} onClick={handlePriceClick}>
                    See Details
                  </span>
                </div>
              </div>
            </Grid>
          </Grid>
          <ActionBar
            viewCallBack={viewCourse}
            viewTooltip="View course"
            editCallBack={editCourse}
            editTooltip="Edit course"
            switchCallBack={changeCourseStatus}
            switchTooltipActive="Deactivate course"
            switchTooltipNotActive="Activate course"
            switchState={courseFront.isActive}
            deleteCallBack={deleteCourse}
            deleteTooltip={"Delete course"}
          />
        </AccordionSummary>
        <AccordionDetails>
          <div className={clsx(!courseFront.isActive && classes.disabled)}>
            {courseExpandedState === CourseExpandedState.PRICE && (
              <CourseFinance finance={courseFront.finance} />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
